import React, {useState} from "react";
import moment from "moment/moment";
import {ILogEntry} from "../../models/LogEntry";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {Badge, IconButton} from "@mui/material";
import {GridColDef, GridRenderCellParams} from "@mui/x-data-grid";
import {GraphQlDataGrid} from "../../../../components/common/grid/GraphQlDataGrid";
import {GenericModal} from "../generic-modal/GenericModal";
import {GET_JOB_STEP_RUN_DETAILS} from "../graphql/queries/gqlGetJobStepRunDetails";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningOutlinedIcon from "@mui/icons-material/WarningOutlined";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {LogEntryModal} from "./LogEntries";
import {CheckCircleOutline, ErrorOutline} from "@mui/icons-material";

interface IJobStepRunDetailsProps {
	jobRunId: number;
	jobStepRunId: number;
}

export const JobStepRunDetails: React.FC<IJobStepRunDetailsProps> = (props) => {
	const [logEntries, setLogEntries] = useState<ILogEntry[]>([]);

	const columns: GridColDef[] = [
		{
			field: "logs",
			headerName: "Logs",
			width: 120,
			renderCell: (params: GridRenderCellParams) => {
				const info = params.row.logEntries.filter(function (element: ILogEntry) {
					return element.logLevel === "INFORMATION";
				}).length;
				const warning = params.row.logEntries.filter(function (
					element: ILogEntry
				) {
					return element.logLevel === "WARNING";
				}).length;
				const error = params.row.logEntries.filter(function (
					element: ILogEntry
				) {
					return element.logLevel === "ERROR";
				}).length;

				return (
					<>
						<IconButton
							aria-label="View Logs"
							onClick={() => setLogEntries(params.row.logEntries)}
						>
							{info > 0 && (
								<>
									<Badge color="info" badgeContent={info} max={1000000}>
										<InfoOutlinedIcon />
									</Badge>
								</>
							)}
							&nbsp;
							{warning > 0 && (
								<>
									<Badge color="warning" badgeContent={warning} max={1000000}>
										<WarningOutlinedIcon />
									</Badge>
								</>
							)}
							&nbsp;
							{error > 0 && (
								<>
									<Badge color="error" badgeContent={error} max={1000000}>
										<ErrorOutlinedIcon />
									</Badge>
								</>
							)}
							&nbsp;
						</IconButton>
					</>
				);
			},
		},

		{ field: "id", headerName: "ID", width: 70 },
		{
			field: "success",
			headerName: "Success",
			width: 20,
			renderHeader: (params) => <CheckCircleOutline color="success" />,
			renderCell: (params) => {
				return params.row.succeeded && <CheckCircleOutline color="success" />;
			},
		},
		{
			field: "failed",
			headerName: "Failed",
			width: 20,
			renderHeader: (params) => <ErrorOutline color="error" />,
			renderCell: (params) => {
				return params.row.failed && <ErrorOutline color="error" />;
			},
		},

		{
			field: "start",
			headerName: "Start",
			width: 200,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<>
						{params.row.start &&
							moment(params.row.start).format("DD-MM-YYYY HH:mm:ss")}
					</>
				);
			},
		},
		{
			field: "finish",
			headerName: "Finish",
			width: 200,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<>
						{params.row.finish &&
							moment(params.row.finish).format("DD-MM-YYYY HH:mm:ss")}
					</>
				);
			},
		},

		{
			field: "description",
			headerName: "Description",
			width: 200,
			renderCell: (params: GridRenderCellParams) => {
				return <>{params.row.description ?? params.row.job.description}</>;
			},
		},
	];

	return (
		<>
			<GraphQlDataGrid
				id={"JobStepRunDetails"}
				enableHeaderFilter
				persistFilters
				persistSorting
				autoHeight={true}
				query={GET_JOB_STEP_RUN_DETAILS}
				graphFunction={"jobStepRunDetails"}
				client={graphQLServices.querytoolClient}
				rowsPerPageOptions={[10, 20, 50]}
				initialPageSize={10}
				columns={columns}
				defaultSortModel={[
					{
						field: "id",
						sort: "desc",
					},
				]}
				rows={[]}
				queryVariables={{ jobStepRunId: props.jobStepRunId }}
			/>
			<GenericModal
				open={logEntries.length > 0}
				onClose={() => setLogEntries([])}
				title={"Logs"}
			>
				<LogEntryModal logEntries={logEntries} />
			</GenericModal>
		</>
	);
};
